import { useState } from "react";
import { ChevronDown } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import SectionLabel from "@/components/site/SectionLabel";
import Seo from "@/components/site/Seo";

const FAQ_DATA = [
  {
    cat: "For Contestants & Speakers",
    qs: [
      {
        q: "Who is eligible to apply?",
        a: "Emerging speakers with a serious message — entrepreneurs, authors, advocates, coaches, community leaders, veterans, educators, faith-based speakers, and people with meaningful lived experience.",
      },
      {
        q: "Do I need to be a polished, professional keynoter?",
        a: "No. We are looking for serious messages, coachable speakers, and the courage to step forward. Polish can be built. Substance cannot be faked.",
      },
      {
        q: "Is there a cost to apply?",
        a: "Application costs and any participation fees will be announced as part of the full application launch. The pilot series is designed to be accessible.",
      },
      {
        q: "How long is each talk?",
        a: "Talk length will be confirmed in the full application packet. Expect a tightly structured competition format that respects both speakers and audience.",
      },
      {
        q: "How does judging work?",
        a: "A structured rubric used by a panel of judges. Specific judges and full rubric details will be published with each event.",
      },
      {
        q: "Will my talk be recorded?",
        a: "Yes. The Next Stage is designed as both a live experience and a media production opportunity. Selected contestants sign a media agreement as part of their participation, and may receive footage of their own talk for personal use.\n\nSpecific media-use terms are detailed in the contestant media agreement. Event-wide media — audience footage, recap content, sizzle reels, and other contestants' talks — remains the property of Use What You Got Publishing, LLC.",
      },
      {
        q: "Will I get feedback?",
        a: "Yes. Substantive feedback is a core part of the experience — not an afterthought.",
      },
      {
        q: "Can I be coached before the event?",
        a: "Selected contestants receive coaching guidance leading into the event. Specific coaching programming will be detailed when the application opens.",
      },
      {
        q: "Where and when are the pilot events?",
        a: "Pilot One: Fall 2026 in Colorado Springs. Pilot Two: Early 2027 in Colorado Springs. Specific venues will be announced as bookings are finalized.",
      },
      {
        q: "What are the prizes?",
        a: "Prize details are being finalized and will be announced with each event. The opportunities tied to the platform — exposure, content, and pipeline — are the largest prizes.",
      },
      {
        q: "I'm not from Colorado Springs. Can I still apply?",
        a: "Yes. Geography is not a disqualifier. Travel and lodging are the responsibility of the contestant unless otherwise announced.",
      },
    ],
  },
  {
    cat: "For Sponsors",
    qs: [
      {
        q: "What sponsorship levels are available?",
        a: "Nine levels from $250 (Supporting Friend) to $5,000 (Presenting Sponsor). See the Sponsor page for the full list.",
      },
      {
        q: "What benefits come with each level?",
        a: "Tier-specific benefit detail is being finalized. Inquire and we will share the current benefit sheet for your selected level.",
      },
      {
        q: "Is sponsorship tax-deductible?",
        a: "No. The Next Stage is produced by Use What You Got Publishing, LLC, a for-profit company. Sponsorship commitments are not tax-deductible charitable contributions.",
      },
      {
        q: "When do sponsorship commitments activate?",
        a: "Commitments activate once minimum operating needs are met and the event is publicly confirmed. If a pilot does not move forward, sponsorship commitments are returned or rolled forward.",
      },
      {
        q: "Can sponsorship be customized?",
        a: "Yes. Choose Open / Custom on the inquiry form and we will design alignment around your goals.",
      },
      {
        q: "Will I get recap content I can use?",
        a: "Yes. Photo, video, and recap assets are core deliverables of the platform.",
      },
    ],
  },
  {
    cat: "For Audience",
    qs: [
      {
        q: "How do I get tickets?",
        a: "Sign up on the Attend page and we will notify you the moment tickets are released for each event.",
      },
      {
        q: "What does a ticket include?",
        a: "Reserved theater seating, full program, and post-event recap access. VIP tickets add premium seating and a pre-show reception.",
      },
      {
        q: "How much do tickets cost?",
        a: "Specific pricing will be announced with each event.",
      },
      {
        q: "Where will the event be held?",
        a: "Colorado Springs. Venues will be announced as bookings are finalized.",
      },
      {
        q: "Is the event accessible?",
        a: "We are committed to selecting accessible venues. Specific accessibility detail will be published with each event listing.",
      },
      {
        q: "Can I bring guests?",
        a: "Yes. Each ticket admits one guest. Bring people whose courage you want to support.",
      },
    ],
  },
  {
    cat: "For Partners & Vendors",
    qs: [
      {
        q: "What partnership types exist?",
        a: "Training, media, business/community, nonprofit alignment, and faith-based partners. See the Partners page for details.",
      },
      {
        q: "How do vendor opportunities work?",
        a: "Vendor opportunities open ahead of each event. Use the contact form and choose Vendor as the inquiry type to be added to the vendor outreach list.",
      },
      {
        q: "Can my organization co-host content?",
        a: "Reach out via the contact form with Partnership selected. We design co-hosted content collaboratively.",
      },
      {
        q: "Do partnerships require sponsorship?",
        a: "No. Partnership and sponsorship are distinct pathways. Partnerships often involve content, audience, or community alignment rather than monetary commitment.",
      },
    ],
  },
  {
    cat: "General",
    qs: [
      {
        q: "Who produces The Next Stage?",
        a: "Use What You Got Publishing, LLC — a for-profit publishing and event production company based in Colorado Springs. Founder and Producer: R. Dale Brunet.",
      },
      {
        q: "Is The Next Stage a nonprofit?",
        a: "No. It is produced by a for-profit LLC. We do not use 501(c)(3) language.",
      },
      {
        q: "Is this a recurring or quarterly event?",
        a: "Not yet. The two pilots are a proof-of-concept. Future cadence will be decided after the pilot series concludes.",
      },
      {
        q: "How can I volunteer?",
        a: "Use the contact form and choose Volunteer as the inquiry type. Volunteer roles will be detailed closer to each event.",
      },
      {
        q: "How can I contact the producer team?",
        a: "Use the contact form or email info@TheNextStageShow.com directly. Phone: (719) 205-6381.",
      },
      {
        q: "How is my information used?",
        a: "We use submission data only to follow up with you regarding The Next Stage. We do not sell or share your information. See the Privacy page.",
      },
    ],
  },
];

function AccordionGroup({ cat, items, idx }) {
  const [openIdx, setOpenIdx] = useState(null);
  const toggle = (i) => setOpenIdx((cur) => (cur === i ? null : i));

  return (
    <div data-testid={`faq-group-${idx}`} className="mb-16">
      <SectionLabel number={String(idx + 1).padStart(2, "0")}>{cat}</SectionLabel>
      <div className="hairline divide-y divide-slate-soft mt-4">
        {items.map((it, i) => {
          const isOpen = openIdx === i;
          return (
            <div key={i}>
              <button
                onClick={() => toggle(i)}
                data-testid={`faq-trigger-${idx}-${i}`}
                aria-expanded={isOpen}
                className="w-full py-6 px-2 flex items-center justify-between text-left gap-6 group"
              >
                <span className="font-shoulders font-bold uppercase tracking-wide text-xl sm:text-2xl text-white group-hover:text-gold transition">
                  {it.q}
                </span>
                <ChevronDown
                  className={`text-gold shrink-0 transition-transform duration-300 ${
                    isOpen ? "rotate-180" : ""
                  }`}
                  size={22}
                />
              </button>
              {isOpen && (
                <div
                  data-testid={`faq-panel-${idx}-${i}`}
                  className="pb-6 px-2 font-manrope text-soft-gray text-base sm:text-lg leading-relaxed whitespace-pre-line"
                >
                  {it.a}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function FAQ() {
  return (
    <div>
      <Seo />
      <PageHero
        pageTitle="FAQ"
        eyebrowNumber="01"
        eyebrow="Frequently Asked"
        title="Questions —"
        highlight="Answered Directly."
        tagline="What is known. What is to be announced. What is the standard."
        description="If your question isn't covered here, reach the team via the contact page."
      />

      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14">
          {FAQ_DATA.map((g, i) => (
            <AccordionGroup key={g.cat} cat={g.cat} items={g.qs} idx={i} />
          ))}
        </div>
      </section>
    </div>
  );
}
